import React from 'react';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorFallback from '@shared/components/ErrorFallback';
import { AuthProvider } from '@features/auth/contexts/AuthContext';
import { readInitialTheme, ThemeProvider } from '@shared/contexts/ThemeContext';
import logger from '@shared/utils/logger';

// Initialize theme class before React paints (ThemeProvider keeps state in sync after mount)
(function initializeTheme() {
  const root = document.documentElement;
  const t = readInitialTheme();
  root.classList.toggle('dark', t === 'dark');
  try {
    if (!localStorage.getItem('theme')) {
      localStorage.setItem('theme', t);
    }
  } catch {
    /* ignore */
  }
})();

// Error logging function for react-error-boundary
// This is called when an error is caught by the ErrorBoundary
const logError = (error, errorInfo) => {
  logger.error('Error caught by ErrorBoundary:', error);
  logger.error('Error info:', errorInfo);
};

// Hide the HTML loading spinner safely - before React even starts
// This runs as soon as this script loads
(function hideLoadingSpinner() {
  try {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hideLoadingSpinner);
      return;
    }
    
    // Try multiple selectors in case the class name varies
    const selectors = ['.loading', '#root .loading', '[class*="loading"]'];
    selectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Check if element exists and is connected to DOM
          if (el && el.parentNode) {
            try {
              el.style.display = 'none';
              // Use remove() safely
              if (el.parentNode) {
                el.remove();
              }
            } catch (e) {
              // Element might have been removed already, ignore
            }
          }
        });
      } catch (e) {
        // Continue to next selector
      }
    });
  } catch (e) {
    // Silently fail - don't block app initialization
  }
})();

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // Don't clear innerHTML - React will handle DOM management
  // Clearing it can cause "deferred DOM Node" errors

  const root = ReactDOM.createRoot(rootElement);

  const AppWithErrorBoundary = () => {
    return (
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={logError}
        onReset={() => {
          // Reset any error state if needed
          window.location.reload();
        }}
        resetKeys={[]}
      >
        <App />
      </ErrorBoundary>
    );
  };

  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <AuthProvider>
          <AppWithErrorBoundary />
        </AuthProvider>
      </ThemeProvider>
    </React.StrictMode>
  );

  setTimeout(() => {
    try {
      const selectors = ['.loading', '#root .loading', '[class*="loading"]'];
      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            if (el && el.parentNode) {
              el.style.display = 'none';
              if (el.parentNode) {
                el.remove();
              }
            }
          });
        } catch (e) {
          // Ignore errors
        }
      });
    } catch (e) {
      // Silently fail
    }
  }, 100);
} catch (error) {
  logger.error('Failed to initialize app:', error);
  logger.error('Error stack:', error.stack);

  // Hide loading spinner on error too
  try {
    const selectors = ['.loading', '#root .loading', '[class*="loading"]'];
    selectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el && el.parentNode) {
            el.style.display = 'none';
            if (el.parentNode) {
              el.remove();
            }
          }
        });
      } catch (e) {
        // Ignore errors
      }
    });
  } catch (e) {
    // Silently fail
  }
  
  // Show fallback UI with detailed error
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        padding: 20px;
        text-align: center;
        font-family: system-ui, -apple-system, sans-serif;
      ">
        <h1 style="color: #e74c3c; margin-bottom: 20px; font-size: 24px;">Failed to initialize app</h1>
        <p style="margin-bottom: 10px; color: #666; font-size: 16px;">${error.message || 'Unknown error'}</p>
        ${error.stack ? `<pre style="text-align: left; background: #f5f5f5; padding: 15px; border-radius: 4px; max-width: 600px; overflow-x: auto; font-size: 12px; margin: 20px 0;">${error.stack}</pre>` : ''}
        <button
          onclick="window.location.reload()"
          style="
            padding: 10px 20px;
            background: #3498db;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            margin-top: 20px;
          "
        >
          Refresh Page
        </button>
      </div>
    `;
  }
}

// Report web vitals
reportWebVitals((metric) => logger.info(metric));
